import { NavLink, useLocation } from "react-router-dom";
import {
  BarChart3,
  ChevronDown,
  History as HistoryIcon,
  LayoutDashboard,
  LogOut,
  ScrollText,
  Settings as SettingsIcon,
  User,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { useAuth } from "@/auth/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  /** Routes supplémentaires sur lesquelles l'entrée reste active (onglets routés). */
  alsoActive?: string[];
}

// Dual-cockpit variante B (owner 2026-07-31) : le menu garde son entrée unique
// « Cockpit » ; les deux cockpits — « Autopilote » (/dashboard, cyan) et
// « Commandes » (/manual, bleu) — sont des ONGLETS routés en tête de la zone
// cockpit (CockpitTabs). L'entrée est active sur les deux routes (alsoActive).
export const NAV: NavItem[] = [
  { to: "/dashboard", label: "Cockpit", Icon: LayoutDashboard, alsoActive: ["/manual"] },
  { to: "/history", label: "Historique", Icon: HistoryIcon },
  { to: "/analytics", label: "Analytique", Icon: BarChart3 },
  { to: "/settings", label: "Réglages", Icon: SettingsIcon },
  { to: "/logs", label: "Journal", Icon: ScrollText },
];

/** Primary navigation links with an active-route highlight. Reused by both
 *  the dashboard TopBar and the secondary-page NavBar. The icons are ALWAYS
 *  rendered — navigation must never disappear below a breakpoint (C2). */
export function NavLinks({
  className,
  compact = false,
}: {
  className?: string;
  /** Compact context (dashboard TopBar, which also hosts the price tile):
   *  labels only appear at lg+; default shows them from sm. */
  compact?: boolean;
}) {
  const { pathname } = useLocation();
  return (
    <nav
      className={cn("flex items-center gap-0.5", className)}
      aria-label="Primary"
    >
      {NAV.map(({ to, label, Icon, alsoActive }) => (
        <NavLink
          key={to}
          to={to}
          title={label}
          className={({ isActive }) =>
            cn(
              "relative inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-all duration-200",
              "text-muted-foreground hover:bg-white/5 hover:text-foreground",
              "outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
              (isActive || alsoActive?.some((p) => pathname.startsWith(p))) &&
                "bg-accent-soft text-foreground shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--color-accent-neon)_30%,transparent)]",
            )
          }
        >
          <Icon className="size-4" aria-hidden />
          <span className={compact ? "hidden lg:inline" : "hidden sm:inline"}>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

/** Brand mark — a live-pulsing pip + wordmark. (The optional subtitle is retained
 *  for callers but the unified header renders the brand without one.) */
export function Brand({ subtitle }: { subtitle?: string }) {
  return (
    <div className="flex shrink-0 items-center gap-2">
      <span
        className="pip-live bg-accent-neon size-2.5 rounded-full shadow-[0_0_10px_var(--color-accent-neon)]"
        aria-hidden
      />
      <strong className="text-gradient tracking-tight">DAX Bot</strong>
      {subtitle && (
        <span className="text-muted-foreground hidden text-sm md:inline">
          / {subtitle}
        </span>
      )}
    </div>
  );
}

/** Initials for the account avatar, from the email local-part ("barro.lissy@…" → "BL"). */
function initialsOf(email: string | null): string {
  if (!email) return "";
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  const letters = parts.length >= 2 ? parts[0][0] + parts[1][0] : local.slice(0, 2);
  return letters.toUpperCase();
}

/**
 * Account avatar + dropdown — the merged email/logout control. The trigger is a
 * compact initials avatar; the menu reveals the full email and the Log out action.
 * Radix owns keyboard + outside-click; the whole email/logout pair is now one element.
 */
export function UserMenu() {
  const { logout, lastEmail } = useAuth();
  const initials = initialsOf(lastEmail);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="user-menu"
          aria-label={lastEmail ? `Menu du compte — ${lastEmail}` : "Menu du compte"}
          title={lastEmail ?? undefined}
          className="text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center gap-1 rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <span className="bg-surface-2 border-border/60 text-foreground inline-flex size-8 items-center justify-center rounded-full border text-xs font-semibold">
            {initials || <User className="size-4" aria-hidden />}
          </span>
          <ChevronDown className="size-3.5" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {lastEmail && (
          <>
            <DropdownMenuLabel className="text-foreground max-w-[16rem] truncate font-medium">
              {lastEmail}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          data-testid="logout"
          onSelect={logout}
          className="text-muted-foreground focus:text-foreground"
        >
          <LogOut aria-hidden />
          Se déconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
